import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
  RefreshCw, 
  Sparkles, 
  Heart,
  ChevronRight,
  Calendar,
  Target,
  Smile,
  Cloud,
  Sun,
  Star,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceInput from './VoiceInput';

// Local Storage functions for weekly reset
const STORAGE_KEY = 'calm_keeper_weekly_resets';

const getWeekKey = () => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${weekNumber}`;
};

const getWeeklyResets = () => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : {};
};

const saveWeeklyReset = (reset) => {
  const resets = getWeeklyResets();
  resets[getWeekKey()] = {
    ...reset,
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(resets));
  return reset;
};

const getCurrentWeekReset = () => {
  const resets = getWeeklyResets();
  return resets[getWeekKey()] || null;
};

const getPastResets = () => {
  const resets = getWeeklyResets();
  const currentWeek = getWeekKey();
  return Object.entries(resets)
    .filter(([key]) => key !== currentWeek)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 4)
    .map(([key, value]) => ({ week: key, ...value }));
};

const WeeklyReset = () => {
  const [step, setStep] = useState(0);
  const [wins, setWins] = useState('');
  const [letGo, setLetGo] = useState('');
  const [gratitude, setGratitude] = useState('');
  const [intentions, setIntentions] = useState('');
  const [selfCare, setSelfCare] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [currentReset, setCurrentReset] = useState(null);
  const [pastResets, setPastResets] = useState([]);

  useEffect(() => {
    const existing = getCurrentWeekReset();
    if (existing) {
      setCurrentReset(existing);
      setWins(existing.wins || '');
      setLetGo(existing.letGo || '');
      setGratitude(existing.gratitude || '');
      setIntentions(existing.intentions || '');
      setSelfCare(existing.selfCare || '');
      setIsComplete(true);
    }
    setPastResets(getPastResets());
  }, []);

  const handleSave = () => {
    const reset = saveWeeklyReset({
      wins: wins.trim(),
      letGo: letGo.trim(),
      gratitude: gratitude.trim(),
      intentions: intentions.trim(),
      selfCare: selfCare.trim(),
    });
    setCurrentReset(reset);
    setIsComplete(true);
    toast.success('Your weekly reset is complete. You\'re ready for a fresh start!');
  };

  const steps = [
    {
      id: 'wins',
      title: 'Celebrate Your Wins',
      subtitle: 'What went well this week, no matter how small?',
      icon: Star,
      color: 'good',
      placeholder: 'I managed to... The kids and I... I took time to...',
      value: wins,
      setValue: setWins,
    },
    {
      id: 'letgo',
      title: 'Let It Go',
      subtitle: 'What do you need to release from this week?',
      icon: Cloud,
      color: 'normal',
      placeholder: 'I\'m letting go of... I forgive myself for... I release the guilt about...',
      value: letGo,
      setValue: setLetGo,
    },
    {
      id: 'gratitude',
      title: 'Gratitude Moment',
      subtitle: 'What are you thankful for?',
      icon: Heart,
      color: 'good',
      placeholder: 'I\'m grateful for... My heart is full because... I appreciate...',
      value: gratitude,
      setValue: setGratitude,
    },
    {
      id: 'intentions',
      title: 'Set Your Intentions',
      subtitle: 'What do you want to focus on next week?',
      icon: Target,
      color: 'normal',
      placeholder: 'Next week I want to... My priority will be... I\'ll focus on...',
      value: intentions,
      setValue: setIntentions,
    },
    {
      id: 'selfcare',
      title: 'Self-Care Promise',
      subtitle: 'What\'s one thing you\'ll do just for you?',
      icon: Smile,
      color: 'good',
      placeholder: 'I promise to... I deserve... I\'ll take time to...',
      value: selfCare,
      setValue: setSelfCare,
    },
  ];

  const getColorClasses = (color) => {
    if (color === 'good') {
      return {
        bg: 'bg-good-bg',
        border: 'border-good-border',
        text: 'text-good-text',
        iconBg: 'bg-good-bg',
      };
    }
    return {
      bg: 'bg-normal-bg',
      border: 'border-normal-border',
      text: 'text-normal-text',
      iconBg: 'bg-normal-bg',
    };
  };

  if (isComplete) {
    return (
      <div className="space-y-6 relative" data-testid="weekly-reset-complete">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 text-3xl opacity-20 pointer-events-none">✨</div>
        <div className="absolute bottom-20 left-0 text-2xl opacity-15 pointer-events-none">🌸</div>
        
        {/* Completion card */}
        <Card className="p-8 bg-gradient-to-br from-good-bg to-sage-mist border-good-border text-center shadow-lifted relative overflow-hidden">
          <div className="absolute top-4 left-4 text-xl opacity-30">🌿</div>
          <div className="absolute bottom-4 right-4 text-xl opacity-30">💫</div>
          
          <div className="w-16 h-16 rounded-full bg-white mx-auto mb-4 flex items-center justify-center shadow-paper">
            <CheckCircle className="w-8 h-8 text-sage" />
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-good-text mb-2">
            You're Reset & Ready
          </h2>
          <p className="text-good-text/80 mb-6 font-accent italic">
            You've set your intentions. Now take it one day at a time.
          </p>
          
          {/* Summary cards */}
          {currentReset && (
            <div className="space-y-3 text-left">
              {currentReset.wins && (
                <div className="p-4 bg-white/60 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-good-text/70 font-accent">This week's wins</span>
                  </div>
                  <p className="text-good-text font-handwritten text-base">{currentReset.wins}</p>
                </div>
              )}
              
              {currentReset.intentions && (
                <div className="p-4 bg-white/60 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-brown" />
                    <span className="text-sm text-good-text/70 font-accent">Next week's focus</span>
                  </div>
                  <p className="text-good-text font-handwritten text-base">{currentReset.intentions}</p>
                </div>
              )}
              
              {currentReset.selfCare && (
                <div className="p-4 bg-white/60 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-pink-400" />
                    <span className="text-sm text-good-text/70 font-accent">Self-care promise</span>
                  </div>
                  <p className="text-good-text font-handwritten text-base">{currentReset.selfCare}</p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Affirmation */}
        <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 text-center">
          <p className="font-serif text-lg text-ink-700 italic">
            "You don't have to be perfect. You just have to keep showing up."
          </p>
          <p className="text-sm text-ink-500 mt-2">— Your weekly reminder 💝</p>
        </Card>
        
        {/* Edit button */}
        <Button
          data-testid="weekly-reset-edit"
          onClick={() => {
            setIsComplete(false);
            setStep(0);
          }}
          variant="outline"
          className="w-full rounded-lg border-stone-300 text-ink-700"
        >
          Edit this week's reset
        </Button>
        
        {/* Past resets */}
        {pastResets.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-serif text-xl text-ink-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brown" />
              Previous weeks
            </h3>
            <div className="space-y-3">
              {pastResets.map((reset, idx) => (
                <Card 
                  key={idx} 
                  className="p-4 bg-paper-light shadow-lifted border border-stone-200"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-lg">📅</span>
                    <span className="text-sm text-ink-500 font-accent italic">
                      {reset.week}
                    </span>
                  </div>
                  {reset.intentions && (
                    <p className="text-sm text-ink-700">
                      <span className="text-ink-500">Focus:</span> {reset.intentions}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const currentStep = steps[step];
  const StepIcon = currentStep.icon;
  const colors = getColorClasses(currentStep.color);

  return (
    <div className="space-y-6 relative" data-testid="weekly-reset-page">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 text-3xl opacity-20 pointer-events-none">🌸</div>
      
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-sage/20 mx-auto mb-4 flex items-center justify-center border border-sage/30">
          <RefreshCw className="w-7 h-7 text-sage-dark" />
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
          Weekly Mental Reset
        </h2>
        <p className="text-ink-500 font-accent italic">
          Reflect, release, and reset for a fresh week 🌿
        </p>
      </div>
      
      {/* Progress */}
      <div className="flex gap-1">
        {steps.map((s, idx) => (
          <div
            key={s.id}
            className={`
              h-2 flex-1 rounded-full transition-all
              ${idx < step ? 'bg-sage' : idx === step ? 'bg-brown' : 'bg-stone-200'}
            `}
          />
        ))}
      </div>
      
      {/* Step indicator */}
      <div className="text-center text-sm text-ink-500 font-accent">
        Step {step + 1} of {steps.length}
      </div>
      
      {/* Current step */}
      <Card className={`p-6 shadow-lifted border-2 ${colors.border} ${colors.bg} animate-fade-in relative overflow-hidden`}>
        {/* Decorative element */}
        <div className="absolute -top-2 right-8 w-12 h-4 bg-white/40 transform rotate-1 rounded-sm" />
        
        <div className="flex items-center gap-3 mb-4 pt-2">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-white/50`}>
            <StepIcon className={`w-6 h-6 ${colors.text}`} />
          </div>
          <div>
            <h3 className="font-serif text-xl text-ink-900">{currentStep.title}</h3>
            <p className="text-sm text-ink-600 font-accent italic">{currentStep.subtitle}</p>
          </div>
        </div>
        
        <div className="flex items-center justify-end mb-1">
          <VoiceInput onTranscription={(text) => currentStep.setValue(prev => prev ? prev + ' ' + text : text)} />
        </div>
        <Textarea
          data-testid={`weekly-reset-input-${currentStep.id}`}
          value={currentStep.value}
          onChange={(e) => currentStep.setValue(e.target.value)}
          placeholder={currentStep.placeholder}
          className="min-h-[140px] border-stone-300 focus:border-brown focus:ring-brown/20 mb-4 font-handwritten text-lg bg-white/50"
        />
        
        <div className="flex gap-3">
          {step > 0 && (
            <Button
              data-testid="weekly-reset-back"
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="rounded-lg border-stone-300 bg-white/50"
            >
              Back
            </Button>
          )}
          
          {step < steps.length - 1 ? (
            <Button
              data-testid="weekly-reset-next"
              onClick={() => setStep(step + 1)}
              className="flex-1 vintage-btn btn-brown rounded-lg"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="weekly-reset-complete"
              onClick={handleSave}
              className="flex-1 vintage-btn btn-sage rounded-lg"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Complete My Reset
            </Button>
          )}
        </div>
      </Card>
      
      {/* Encouragement */}
      <Card className="p-4 bg-paper-aged border border-stone-200 text-center">
        <p className="text-sm text-ink-600 font-accent italic">
          {step === 0 && "Take a moment to recognize your efforts this week 💛"}
          {step === 1 && "It's okay to let go. You don't have to carry everything 🍂"}
          {step === 2 && "Gratitude shifts our perspective to what matters most 🌸"}
          {step === 3 && "Small, gentle goals are more sustainable than big ones 🎯"}
          {step === 4 && "You can't pour from an empty cup. This matters 💝"}
        </p>
      </Card>
      
      {/* Skip option */}
      <div className="text-center">
        <Button
          data-testid="weekly-reset-skip"
          onClick={handleSave}
          variant="ghost"
          className="text-ink-500 hover:text-ink-700 font-accent italic"
        >
          Skip and finish
        </Button>
      </div>
    </div>
  );
};

export default WeeklyReset;
